import {
  getResidualPortfolio,
  getResidualVolume,
  getResidualScorecard,
  getResidualDataV2,
  getResidualDataV2Export,
  getResidualFilterData,
  getPaymentAuthorizationReport,
  getAgentAccountStatementReport,
} from "../../api";
import toast from "react-hot-toast";

// Core data fetching APIs
export const fetchResidualPortfolio = async (filters) => {
  try {
    const payload = filters
      ? {
          start_date: filters.dateRange?.startDate,
          end_date: filters.dateRange?.endDate,
          offices: filters.offices || [],
          branches: filters.branches || [],
          repCodes: filters.repCodes || [],
          searchTerm: filters.searchTerm || "",
          org_filter: filters.orgFilter || "",
        }
      : {};
    const data = await getResidualPortfolio(payload);
    return data.data;
  } catch (error) {
    toast.error(error.data.error);
    throw error;
  }
};

export const fetchResidualVolume = async (filters) => {
  try {
    const data = await getResidualVolume(filters);
    return data.data;
  } catch (error) {
    toast.error(error.data.error);
    throw error;
  }
};

export const fetchResidualsData = async (filters, isExport) => {
  try {
    if (isExport) {
      const params = {
        start_date: filters.dateRange.startDate,
        end_date: filters.dateRange.endDate,
        offices: filters.offices || [],
        branches: filters.branches || [],
        repCodes: filters.repCodes || [],
        cards: filters.cards || [],
        searchTerm: filters.searchTerm || "",
        org_filter: filters.orgFilter || "",
        sort_by: filters.sort?.field || "dba",
        sort_direction: filters.sort?.direction || "asc",
        display_mode: filters.displayMode || "hierarchy",
        export: true,
      };

      const response = await getResidualDataV2Export(params);
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const date = new Date(filters.dateRange.startDate);
      const monthNames = [
        "jan",
        "feb",
        "mar",
        "apr",
        "may",
        "jun",
        "jul",
        "aug",
        "sept",
        "oct",
        "nov",
        "dec",
      ];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      const fileName = `residuals-${month}-${year}.csv`;

      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    }

    const response = await getResidualDataV2({
      start_date: filters.dateRange.startDate,
      end_date: filters.dateRange.endDate,
      offices: filters.offices || [],
      branches: filters.branches || [],
      repCodes: filters.repCodes || [],
      cards: filters.cards || [],
      searchTerm: filters.searchTerm || "",
      org_filter: filters.orgFilter || "",
      page: filters.pagination?.page || 1,
      per_page:
        filters.pagination?.per_page ||
        (filters.displayMode === "flat" ? 50 : 10),
      sort_by: filters.sort?.field || "dba",
      sort_direction: filters.sort?.direction || "asc",
      display_mode: filters.displayMode || "hierarchy",
      export: false,
    });

    return {
      data: response.data || [],
      pagination: response.pagination || {
        total: 0,
        per_page: 10,
        current_page: 1,
        last_page: 1,
      },
    };
  } catch (error) {
    toast.error(error.data?.error || "Failed to fetch residuals data");
    throw error;
  }
};

export const fetchResidualMerchantExport = async (filters, merchant) => {
  try {
    const params = {
      start_date: filters.dateRange.startDate,
      end_date: filters.dateRange.endDate,
      offices: filters.offices || [],
      branches: filters.branches || [],
      repCodes: filters.repCodes || [],
      cards: filters.cards || [],
      searchTerm: filters.searchTerm || "",
      org_filter: filters.orgFilter || "",
      sort_by: filters.sort?.field || "dba",
      sort_direction: filters.sort?.direction || "asc",
      export: true,
      merchant_mid: merchant.mid,
      merchant_office: merchant.office,
      merchant_branch: merchant.branch,
      merchant_rep_code: merchant.rep_code,
    };

    const response = await getResidualDataV2Export(params);
    const blob = response.data;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const safeMid = String(merchant.mid ?? "export").replace(/[^A-Za-z0-9_-]+/g, "-");
    link.setAttribute("download", `residual-${safeMid}.csv`);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    let message = "Failed to download residual row";
    const data = error.response?.data;
    if (data instanceof Blob) {
      try {
        const text = await data.text();
        const parsed = JSON.parse(text);
        if (typeof parsed.message === "string") {
          message = parsed.message;
        }
      } catch (_) {
        /* keep default */
      }
    } else if (typeof data?.message === "string") {
      message = data.message;
    } else if (typeof error.data?.error === "string") {
      message = error.data.error;
    }
    toast.error(message);
    throw error;
  }
};

export const fetchResidualScorecard = async (filters) => {
  try {
    const data = await getResidualScorecard(filters);
    return data.data;
  } catch (error) {
    toast.error(error.data.error);
    throw error;
  }
};

export const fetchResidualFilterData = async () => {
  try {
    const data = await getResidualFilterData();
    return data.data;
  } catch (error) {
    toast.error(error.data.error);
    throw error;
  }
};

export const fetchPaymentAuthorizationReport = async (params) => {
  try {
    return await getPaymentAuthorizationReport(params);
  } catch (error) {
    const message =
      typeof error === "string"
        ? error
        : (error?.data?.message ??
          error?.data?.error ??
          "Failed to load report");
    toast.error(message);
    throw error;
  }
};

export const fetchAgentAccountStatementReport = async (params) => {
  try {
    return await getAgentAccountStatementReport(params);
  } catch (error) {
    const message =
      typeof error === "string"
        ? error
        : (error?.data?.message ??
          error?.data?.error ??
          "Failed to load agent account statement");
    toast.error(message);
    throw error;
  }
};
